import React from 'react';
import PropTypes from 'prop-types';
import ReactModal from 'react-modal';
import styles from './admin-modal.css';
import supabase from '../../lib/supabase-client.js';

const apiUrl = process.env.AIPNT_API_URL;

class AdminModal extends React.Component {
    constructor (props) {
        super(props);
        this.state = {
            adminEmail: '',
            adminPassword: '',
            supabaseToken: null,
            loading: false,
            error: '',
            
            newUserEmail: '',
            newUserPassword: '',
            userCreationSuccess: '',
            
            generatedKey: '',
            keyExpiryHours: '24',
            generatedKeyExpiry: null
        };
        this.handleLogin = this.handleLogin.bind(this);
        this.handleCreateUser = this.handleCreateUser.bind(this);
        this.handleCreateKey = this.handleCreateKey.bind(this);
        this.closeModal = this.closeModal.bind(this);
    }

    async handleLogin (event) {
        event.preventDefault();
        this.setState({loading: true, error: ''});
        try {
            if (!supabase) throw new Error('Supabase configuration is missing.');
            const {data, error} = await supabase.auth.signInWithPassword({
                email: this.state.adminEmail,
                password: this.state.adminPassword
            });
            if (error) throw error;
            this.setState({supabaseToken: data.session.access_token, loading: false});
        } catch (error) {
            this.setState({error: error.message, loading: false});
        }
    }

    async handleCreateUser (event) {
        event.preventDefault();
        this.setState({loading: true, error: '', userCreationSuccess: ''});
        try {
            const response = await fetch(`${apiUrl}/api/admin/users`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${this.state.supabaseToken}`
                },
                body: JSON.stringify({
                    email: this.state.newUserEmail,
                    password: this.state.newUserPassword
                })
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || data.message || 'Failed to create user');
            
            this.setState({
                userCreationSuccess: `Successfully created admin: ${data.email}`,
                newUserEmail: '',
                newUserPassword: '',
                loading: false
            });
        } catch (error) {
            this.setState({error: error.message, loading: false});
        }
    }

    async handleCreateKey () {
        this.setState({loading: true, error: '', generatedKey: '', generatedKeyExpiry: null});
        try {
            const expiresInHours = parseInt(this.state.keyExpiryHours, 10);
            const response = await fetch(`${apiUrl}/api/admin/licences`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${this.state.supabaseToken}`
                },
                body: JSON.stringify({
                    tokenGrant: 50000,
                    expiresInHours: expiresInHours > 0 ? expiresInHours : null
                })
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || data.message || 'Failed to generate key');
            
            this.setState({
                generatedKey: data.activationCode,
                generatedKeyExpiry: data.expiresAt,
                loading: false
            });
        } catch (error) {
            this.setState({error: error.message, loading: false});
        }
    }

    closeModal () {
        // Clear token on close for security
        this.setState({supabaseToken: null});
        this.props.onRequestClose();
    }

    renderLogin () {
        return (
            <div className={styles.modalContent}>
                <div className={styles.header}>
                    <h2 className={styles.title}>Admin Verification</h2>
                    <p className={styles.subtitle}>Please verify your administrator credentials</p>
                </div>
                {this.state.error && <div className={styles.errorMessage}>{this.state.error}</div>}
                <form onSubmit={this.handleLogin}>
                    <div className={styles.inputGroup}>
                        <label className={styles.label}>Admin Email</label>
                        <input
                            className={styles.input}
                            required
                            type="email"
                            value={this.state.adminEmail}
                            onChange={e => this.setState({adminEmail: e.target.value})}
                        />
                    </div>
                    <div className={styles.inputGroup}>
                        <label className={styles.label}>Password</label>
                        <input
                            className={styles.input}
                            required
                            type="password"
                            value={this.state.adminPassword}
                            onChange={e => this.setState({adminPassword: e.target.value})}
                        />
                    </div>
                    <button
                        className={styles.submitButton}
                        type="submit"
                        disabled={this.state.loading}
                    >
                        {this.state.loading ? 'Verifying...' : 'Verify Identity'}
                    </button>
                    <button
                        className={styles.submitButton}
                        style={{background: '#666', marginTop: '10px'}}
                        type="button"
                        onClick={this.closeModal}
                    >
                        Cancel
                    </button>
                </form>
            </div>
        );
    }

    renderDashboard () {
        return (
            <div
                className={styles.modalContent}
                style={{width: '700px'}}
            >
                <div className={styles.header}>
                    <h2 className={styles.title}>Admin Dashboard</h2>
                    <p className={styles.subtitle}>Manage Users and Device Keys</p>
                </div>
                
                {this.state.error && <div className={styles.errorMessage}>{this.state.error}</div>}
                {this.state.userCreationSuccess && <div className={styles.successMessage}>{this.state.userCreationSuccess}</div>}

                <div className={styles.section}>
                    <h3 className={styles.sectionTitle}>Generate Activation Key</h3>
                    <p className={styles.subtitle}>Create a one-time-use activation code with a 50,000-token grant.</p>
                    <div className={styles.inputGroup}>
                        <label className={styles.label}>Code Validity (hours)</label>
                        <input
                            className={styles.input}
                            type="number"
                            min="1"
                            max="720"
                            placeholder="24"
                            value={this.state.keyExpiryHours}
                            onChange={e => this.setState({keyExpiryHours: e.target.value})}
                        />
                    </div>
                    <button
                        className={styles.submitButton}
                        onClick={this.handleCreateKey}
                        disabled={this.state.loading}
                    >
                        Generate New Key
                    </button>
                    {this.state.generatedKey && (
                        <div className={styles.generatedCodeBox}>
                            <div style={{marginBottom: '5px', fontSize: '12px'}}>Activation Code (one-time use):</div>
                            <div className={styles.generatedCode}>{this.state.generatedKey}</div>
                            {this.state.generatedKeyExpiry && (
                                <div style={{marginTop: '8px', fontSize: '11px', color: '#ff9800'}}>
                                    Expires: {new Date(this.state.generatedKeyExpiry).toLocaleString()}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <div className={styles.section}>
                    <h3 className={styles.sectionTitle}>Create Administrator Account</h3>
                    <form onSubmit={this.handleCreateUser}>
                        <div className={styles.inputGroup}>
                            <label className={styles.label}>New Admin Email</label>
                            <input
                                className={styles.input}
                                required
                                type="email"
                                value={this.state.newUserEmail}
                                onChange={e => this.setState({newUserEmail: e.target.value})}
                            />
                        </div>
                        <div className={styles.inputGroup}>
                            <label className={styles.label}>New Admin Password</label>
                            <input
                                className={styles.input}
                                required
                                type="password"
                                value={this.state.newUserPassword}
                                onChange={e => this.setState({newUserPassword: e.target.value})}
                            />
                        </div>
                        <button
                            className={styles.submitButton}
                            type="submit"
                            disabled={this.state.loading}
                        >
                            Create Account
                        </button>
                    </form>
                </div>

                <button
                    className={styles.submitButton}
                    style={{background: '#666', alignSelf: 'center', width: '200px'}}
                    onClick={this.closeModal}
                >
                    Close Dashboard
                </button>
            </div>
        );
    }

    render () {
        return (
            <ReactModal
                isOpen
                className={styles.modalContent}
                style={{
                    overlay: {
                        backgroundColor: 'rgba(0, 0, 0, 0.75)',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        zIndex: 1000
                    },
                    content: {
                        position: 'static',
                        border: 'none',
                        background: 'transparent',
                        padding: 0
                    }
                }}
                contentLabel="Admin Dashboard"
                onRequestClose={this.closeModal}
            >
                {this.state.supabaseToken ? this.renderDashboard() : this.renderLogin()}
            </ReactModal>
        );
    }
}

AdminModal.propTypes = {
    onRequestClose: PropTypes.func.isRequired
};

export default AdminModal;
